import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Trash2, UserPlus, UserRoundCheck, UserRoundX } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import BoardWorkspace from "@/components/BoardWorkspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/users`;
const roles = [
  ["office_admin", "Office/Admin"],
  ["project_manager", "Project Manager"],
  ["supervisor", "Supervisor"],
];
const blankUser = {
  name: "",
  email: "",
  role: "supervisor",
  temporary_password: "",
};

const apiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg)
      .filter((message) => typeof message === "string");
    return messages.join("; ") || fallback;
  }
  return typeof detail === "string" ? detail : fallback;
};

export default function UserManagementPage() {
  const { canManageUsers, user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(blankUser);
  const [isCreating, setIsCreating] = useState(false);
  const load = useCallback(
    async () => setAccounts((await axios.get(API)).data),
    [],
  );
  useEffect(() => {
    if (canManageUsers)
      load().catch(() => toast.error("Users could not be loaded."));
  }, [canManageUsers, load]);
  if (!canManageUsers) return <Navigate replace to="/master-project-board" />;
  const create = async (event) => {
    event.preventDefault();
    setIsCreating(true);
    try {
      await axios.post(API, form);
      setForm(blankUser);
      toast.success("User created with a temporary password.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "User could not be created.");
    } finally {
      setIsCreating(false);
    }
  };
  const update = async (id, changes) => {
    try {
      await axios.patch(`${API}/${id}`, changes);
      toast.success("User account updated.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "User could not be updated.");
    }
  };
  const setTemporaryPassword = async (id, temporaryPassword) => {
    try {
      await axios.patch(`${API}/${id}/temporary-password`, {
        temporary_password: temporaryPassword,
      });
      toast.success("Temporary password set. Share it once through your secure channel.");
      await load();
      return { ok: true };
    } catch (error) {
      const message = apiErrorMessage(
        error,
        "Temporary password could not be set.",
      );
      toast.error(message);
      return { message, ok: false };
    }
  };
  return (
    <BoardWorkspace
      eyebrow="Company administration"
      headingTestId="user-management-heading"
      subtitle={
        "Manage company accounts. Company Owner/Admin access is assigned "
        + "only by the Platform Owner."
      }
      title="Users"
    >
      <section
        className="user-management-create"
        data-testid="user-management-create-panel"
      >
        <div className="user-management-section-heading">
          <div>
            <span className="operations-kicker">New company account</span>
            <h2>Add a JFM user</h2>
          </div>
          <p>
            New users change their temporary password after their first sign-in. The Platform Owner
            assigns Company Owner/Admin access.
          </p>
        </div>
        <form
          className="user-create-form"
          data-testid="user-create-form"
          onSubmit={create}
        >
          <label>
            Full name
            <input
              data-testid="user-create-name-input"
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
              required
              value={form.name}
            />
          </label>
          <label>
            Email address
            <input
              data-testid="user-create-email-input"
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
              required
              type="email"
              value={form.email}
            />
          </label>
          <label>
            Role
            <select
              data-testid="user-create-role-select"
              onChange={(event) =>
                setForm({ ...form, role: event.target.value })
              }
              value={form.role}
            >
              {roles.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Temporary password
            <input
              data-testid="user-create-temporary-password-input"
              minLength={12}
              onChange={(event) =>
                setForm({ ...form, temporary_password: event.target.value })
              }
              required
              type="password"
              value={form.temporary_password}
            />
          </label>
          <button
            className="invoice-primary-button"
            data-testid="user-create-submit-button"
            disabled={isCreating}
            type="submit"
          >
            <UserPlus size={16} />
            {isCreating ? "Creating…" : "Create user"}
          </button>
        </form>
      </section>
      <section
        className="user-management-list"
        data-testid="user-management-list"
      >
        <div className="user-management-section-heading">
          <div>
            <span className="operations-kicker">JFM company accounts</span>
            <h2>People and access</h2>
          </div>
          <p>
            {accounts.filter((account) => account.active).length} active
            accounts
          </p>
        </div>
        <div className="user-account-grid">
          {accounts.map((account) => (
            <Account
              account={account}
              canResetCompanyPasswords={user?.role === "owner_admin"}
              currentUserId={user.id}
              key={account.id}
              onSetTemporaryPassword={setTemporaryPassword}
              onUpdate={update}
            />
          ))}
        </div>
      </section>
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}

function Account({
  account,
  canResetCompanyPasswords,
  currentUserId,
  onSetTemporaryPassword,
  onUpdate,
}) {
  const [role, setRole] = useState(account.role);
  const [isPasswordEditorOpen, setIsPasswordEditorOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const current = account.id === currentUserId;
  const canManagePassword = (
    canResetCompanyPasswords
    && !current
    && account.active
    && account.role !== "owner_admin"
  );
  const saveTemporaryPassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    if (temporaryPassword !== confirmation) {
      const message = "Temporary password confirmation does not match.";
      setPasswordError(message);
      toast.error(message);
      return;
    }
    setIsSavingPassword(true);
    const result = await onSetTemporaryPassword(account.id, temporaryPassword);
    setIsSavingPassword(false);
    if (!result.ok) {
      setPasswordError(result.message);
      return;
    }
    setTemporaryPassword("");
    setConfirmation("");
    setIsPasswordEditorOpen(false);
  };
  return (
    <article
      className="user-account-row"
      data-testid={`user-account-row-${account.id}`}
    >
      <div className="user-account-identity">
        <strong data-testid={`user-account-name-${account.id}`}>
          {account.name}
        </strong>
        <span>{account.email}</span>
        {account.must_change_password && (
          <small>Temporary password pending change</small>
        )}
      </div>
      <label>
        Role
        <select
          data-testid={`user-role-select-${account.id}`}
          disabled={current || account.role === "owner_admin"}
          onChange={(event) => setRole(event.target.value)}
          value={role}
        >
          {account.role === "owner_admin" && (
            <option value="owner_admin">Company Owner/Admin — Platform assigned</option>
          )}
          {roles.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <div className="user-account-status">
        <span data-testid={`user-account-status-${account.id}`}>
          {account.active ? "Active" : "Deactivated"}
        </span>
        <small>{account.role.replaceAll("_", " ")}</small>
      </div>
      <div className="user-account-actions">
        {!current && account.role !== "owner_admin" && (
          <button
            data-testid={`user-save-role-${account.id}`}
            disabled={role === account.role}
            onClick={() => onUpdate(account.id, { role })}
            type="button"
          >
            Save role
          </button>
        )}
        {!current && (
          <button
            className={account.active ? "user-deactivate-button" : "user-activate-button"}
            data-testid={`user-toggle-active-${account.id}`}
            onClick={() => {
              if (account.active) {
                setShowDeleteConfirmation(true);
              } else {
                onUpdate(account.id, { active: true });
              }
            }}
            type="button"
          >
            {account.active ? (
              <UserRoundX size={15} />
            ) : (
              <UserRoundCheck size={15} />
            )}
            {account.active ? "Deactivate" : "Reactivate"}
          </button>
        )}
        {canManagePassword && (
          <button
            data-testid={`user-set-temporary-password-${account.id}`}
            disabled={isSavingPassword}
            onClick={() => {
              setPasswordError("");
              setIsPasswordEditorOpen((value) => !value);
            }}
            type="button"
          >
            <KeyRound size={15} />
            Set temporary password
          </button>
        )}
        {current && (
          <small data-testid={`user-current-session-${account.id}`}>
            Current session
          </small>
        )}
      </div>
      {isPasswordEditorOpen && (
        <form
          className="user-temporary-password-form"
          data-testid={`user-temporary-password-form-${account.id}`}
          onSubmit={saveTemporaryPassword}
        >
          <p>
            Share this temporary password once through your secure channel. It is never shown again,
            and the user must replace it after sign-in before accessing company operations.
          </p>
          <label htmlFor={`user-temporary-password-input-${account.id}`}>
            Temporary password
            <input
              data-testid={`user-temporary-password-input-${account.id}`}
              id={`user-temporary-password-input-${account.id}`}
              minLength="12"
              onChange={(event) => setTemporaryPassword(event.target.value)}
              placeholder="Temporary password"
              required
              type="password"
              value={temporaryPassword}
            />
          </label>
          <label htmlFor={`user-temporary-password-confirmation-${account.id}`}>
            Confirm temporary password
            <input
              data-testid={`user-temporary-password-confirmation-${account.id}`}
              id={`user-temporary-password-confirmation-${account.id}`}
              minLength="12"
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder="Confirm temporary password"
              required
              type="password"
              value={confirmation}
            />
          </label>
          {passwordError && (
            <p
              data-testid={`user-temporary-password-error-${account.id}`}
              role="alert"
            >
              {passwordError}
            </p>
          )}
          <button
            data-testid={`user-save-temporary-password-${account.id}`}
            disabled={isSavingPassword}
            type="submit"
          >
            {isSavingPassword ? "Setting…" : "Save temporary password"}
          </button>
        </form>
      )}
      {showDeleteConfirmation && (
        <div
          className="user-delete-confirmation"
          data-testid={`user-delete-confirmation-${account.id}`}
          role="alert"
        >
          <strong>Deactivate {account.name}?</strong>
          <p>
            This stops sign-in while preserving historical assignments, records, and audit history.
          </p>
          <button
            className="user-deactivate-button"
            data-testid={`user-confirm-delete-${account.id}`}
            onClick={() => onUpdate(account.id, { active: false })}
            type="button"
          >
            <Trash2 size={15} />
            Confirm deactivation
          </button>
          <button
            data-testid={`user-cancel-delete-${account.id}`}
            onClick={() => setShowDeleteConfirmation(false)}
            type="button"
          >
            Cancel
          </button>
        </div>
      )}
    </article>
  );
}
